import * as paseto from '@/utils/paseto';
import { themeColor } from '@/utils/theme';
import { Controller } from '@hotwired/stimulus';
import {
  BarController,
  BarElement,
  Chart,
  LinearScale,
  TimeScale,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-luxon';

Chart.register(BarController, BarElement, TimeScale, LinearScale, Tooltip);

export default class extends Controller {
  static targets = ['chart', 'total', 'error'];

  #chart;

  // Keep track of pending requests to avoid rendering with stale data when the
  // component is disconnected and connected again before the request completes.
  #load;

  async connect() {
    const load = (this.#load = this.#fetchStatistics());

    try {
      const body = await load;
      if (this.#load !== load) return;

      this.#renderTotal(body.total);
      this.#renderChart(body.data || []);
    } catch (error) {
      if (this.#load !== load) return;

      console.error('Acture Fit: could not load the statistics', error);
      this.errorTarget.hidden = false;
    }
  }

  disconnect() {
    this.#load = undefined;
    this.#chart?.destroy();
    this.#chart = undefined;
  }

  async #fetchStatistics() {
    const { dataUrl } = this.chartTarget.dataset;

    const response = await paseto.fetch(dataUrl, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(await response.text());

    return response.json();
  }

  get #locale() {
    return document.documentElement.lang;
  }

  #renderTotal(total) {
    if (!Number.isFinite(total)) return;

    this.totalTarget.textContent = new Intl.NumberFormat(this.#locale).format(
      total,
    );
  }

  #renderChart(values) {
    // Reset canvas in case of restored pages
    Chart.getChart(this.chartTarget)?.destroy();

    const data = values.map(({ date, value }) => ({
      x: new Date(date),
      y: value,
    }));

    this.#chart = new Chart(this.chartTarget, {
      type: 'bar',
      data: {
        datasets: [
          {
            data,
            backgroundColor: themeColor(this.element, 'primary-900'),
            borderWidth: 0,
            categoryPercentage: 1.0,
            barPercentage: 0.85,
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        animation: { duration: 0 },
        scales: {
          x: {
            type: 'time',
            time: { unit: 'day' },
            adapters: { date: { locale: this.#locale, zone: 'UTC' } },
            border: { display: false },
            grid: { display: false },
            ticks: {
              color: '#52525B',
              font: { size: 12, weight: 350 },
              padding: 0,
            },
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: {
              display: true,
              color: '#E4E4E7',
              drawTicks: true,
            },
            ticks: {
              color: '#52525B',
              font: { size: 12, weight: 350 },
              padding: 6,
              maxTicksLimit: 4,
              precision: 0,
            },
            position: 'right',
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
      },
    });
  }
}
